import React from "react";
import { useRouter } from "next/navigation";

import { Action, Category } from "@/constants/events";
import { Location } from "@/custom-types/Location";
import { FulfillmentPreference } from "@/custom-types/User";
import { useEventTracker } from "@/hooks/useEventTracker";
import useUserFulfillmentPreference from "@/hooks/useUserFulfillmentPreference";
import { updateLocation } from "@/redux/action-creators/location";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getLocationState } from "@/redux/selectors/location";
import { convertGooglePlaceIdToLocation } from "@/utils/convertGoogleGeocoderToLocation";

import DeliveryValidationModal from "@/components/botanic/DeliveryValidationModal";

const DELIVERY_CTA_TEXT = "shop delivery products";
const PICKUP_CTA_TEXT = "shop pickup products";

type AddressCaptureModalProps = {
  isVisible: boolean;
  onDismissed: () => void;
  returnFocusRef: React.RefObject<HTMLElement | null>;
  isDeliveryMode?: boolean;
  eventCategory?: Category;
};

const AddressCaptureModal = ({
  isVisible,
  onDismissed,
  returnFocusRef,
  isDeliveryMode = true,
  eventCategory = Category.deliveryAddressModal,
}: AddressCaptureModalProps) => {
  const userLocation = useSelector(getLocationState);
  const dispatch = useDispatch();
  const { publishEvent } = useEventTracker();
  const { setUserFulfillmentPreference } = useUserFulfillmentPreference();
  const { refresh } = useRouter();
  const CTA_TEXT = isDeliveryMode ? DELIVERY_CTA_TEXT : PICKUP_CTA_TEXT;

  React.useEffect(() => {
    // FEC TrackImpression always fires even when modal is not visible, so do this instead.
    if (isVisible) {
      publishEvent({
        action: Action.impression,
        category: eventCategory,
        label: CTA_TEXT,
      });
    }
  }, [isVisible]);

  const handleSetAddress = (newAddress: Location) => {
    setNewAddress(newAddress);
    publishEvent({
      action: Action.click,
      category: eventCategory,
      label: CTA_TEXT,
    });
    onDismissed();
  };

  const handleDismiss = () => {
    publishEvent({
      action: Action.click,
      category: eventCategory,
      label: "dismiss",
    });
    onDismissed();
  };

  const setNewAddress = async (newAddress: Location) => {
    const placeId = newAddress?.placeId || "";

    const location = await convertGooglePlaceIdToLocation(placeId);
    const cookieLocation = { ...newAddress, ...location };

    updateLocation(dispatch, cookieLocation);
    if (isDeliveryMode) {
      setUserFulfillmentPreference(FulfillmentPreference.DELIVERY);
    }

    refresh();
  };

  return (
    <DeliveryValidationModal
      data-testid="address-capture-modal"
      defaultAddress={userLocation}
      handlePrimaryAction={handleSetAddress}
      onDismissed={handleDismiss}
      primaryActionLabel={CTA_TEXT}
      returnFocusRef={returnFocusRef}
      showModal={isVisible}
      width={500}
      isDeliveryMode={isDeliveryMode}
    />
  );
};

export default AddressCaptureModal;
